import { Table } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'

const MIN_RESIZE_WIDTH = 80

const normalizeWidth = (width) => {
    if (typeof width === 'number') return width
    if (typeof width === 'string' && width.endsWith('px')) {
        const parsedWidth = Number(width.replace('px', ''))
        return Number.isNaN(parsedWidth) ? width : parsedWidth
    }
    return width
}

const normalizeColumns = (columns = []) => {
    return columns.map((column) => ({
        ...column,
        width: normalizeWidth(column.width),
        children: column.children ? normalizeColumns(column.children) : undefined,
    }))
}

const updateColumnByPath = (columns, path, updater) => {
    const [head, ...tail] = path
    return columns.map((column, index) => {
        if (index !== head) return column
        if (tail.length === 0) return updater(column)
        return {
            ...column,
            children: updateColumnByPath(column.children || [], tail, updater),
        }
    })
}

const ResizableTitle = ({ onResize, width, children, ...restProps }) => {
    if (!onResize || !width) {
        return <th {...restProps}>{children}</th>
    }

    const onMouseDown = (event) => {
        event.preventDefault()
        event.stopPropagation()

        const startX = event.clientX
        const startWidth = width

        const onMouseMove = (moveEvent) => {
            const nextWidth = Math.max(MIN_RESIZE_WIDTH, startWidth + moveEvent.clientX - startX)
            onResize(nextWidth)
        }

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }

    return (
        <th {...restProps} style={{ ...restProps.style, position: 'relative' }}>
            {children}
            <div
                role="presentation"
                onMouseDown={onMouseDown}
                style={{
                    position: 'absolute',
                    top: "24%",
                    right: -4,
                    width: 1,
                    height: '50%',
                    cursor: 'col-resize',
                    zIndex: 1,
                    userSelect: 'none',
                    border: '1px solid #c8c8c8',
                    backgroundColor: '#ffffff',
                }}
            />
        </th>
    )
}

const makeResizableColumns = (columns = [], onResize, path = []) => {
    return columns.map((column, index) => {
        const currentPath = [...path, index]
        const withChildren = column.children
            ? {
                ...column,
                children: makeResizableColumns(column.children, onResize, currentPath),
            }
            : { ...column }

        if (!column.resizable || !column.width) return withChildren

        return {
            ...withChildren,
            onHeaderCell: () => ({
                width: column.width,
                onResize: (nextWidth) => onResize(currentPath, nextWidth),
            }),
        }
    })
}

const hasResizableColumn = (columns = []) => {
    return columns.some((column) => {
        if (column.resizable && column.width) return true
        return column.children ? hasResizableColumn(column.children) : false
    })
}

const UGTable = ({ columns = [], pagination, size = 'small', components, ...rest }) => {
    const [resizableColumns, setResizableColumns] = useState(() => normalizeColumns(columns))

    useEffect(() => {
        setResizableColumns(normalizeColumns(columns))
    }, [columns])

    const handleResize = (path, width) => {
        setResizableColumns((previousColumns) => updateColumnByPath(previousColumns, path, (column) => ({
            ...column,
            width,
        })))
    }

    const finalColumns = useMemo(
        () => makeResizableColumns(resizableColumns, handleResize),
        [resizableColumns]
    )

    const tableComponents = useMemo(() => {
        const base = components || undefined
        if (!hasResizableColumn(resizableColumns)) return base

        return {
            ...(base || {}),
            header: {
                ...(base?.header || {}),
                cell: ResizableTitle,
            },
        }
    }, [components, resizableColumns])

    return (
        <Table
            size={size}
            {...rest}
            columns={finalColumns}
            components={tableComponents}
            pagination={pagination ? { ...pagination, showSizeChanger: false } : false}
        />
    )
}

export default UGTable